/**
 * ChatPage — top-level chat (direct messaging) page.
 *
 * Page + feature-hook split: see `presentation.md` § "Page + feature-hook
 * split" for the canonical page-vs-hook ownership pattern and the lifecycle-
 * bridge used here. Route `/chat/:peerId`; in-app nav forwards the full
 * `ChatUsersInfoEntity` via `location.state`, deep-link / refresh path falls
 * back to the hook's `onPageInit` rehydration.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { useLocale } from '@/presentation/contexts/LocaleContext';

import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeyboardInset } from '@/presentation/hooks/useKeyboardInset';
import { useMeasuredHeight } from '@/presentation/hooks/useMeasuredHeight';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';

import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { SubscribeUserSheet } from '@/presentation/feature/userDetails/components/subscribeUserSheet/SubscribeUserSheet';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { ChatActionsSheet } from './components/chatActionsSheet/ChatActionsSheet';
import { launchVideoCall } from '@/presentation/feature/videoCall/utils/launchVideoCall';
import { qaAttr } from '@/presentation/utils/qaAttr';

import { generateUuidV1 } from '@/domain/utils/uuidUtils';

import type { RootState } from '@/presentation/store';

import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { ChatUtils } from '@/domain/utils/chatUtils';

import { useChat } from './hooks/useChat';
import { ChatAppBar } from './components/ChatAppBar';
import { NoInternetBanner } from './components/NoInternetBanner';
import { ChatComposer } from './components/ChatComposer';
import { ChatMessagesRegion } from './components/ChatMessagesRegion';

export const ChatPage = () => {
  const { peerId: peerIdParam } = useParams<{ peerId: string }>();
  const location = useLocation();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { expauseLocalizations } = useExpauseLocalizations();
  const { l10n } = useLocale();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Canonical screen-key wiring — captured ONCE on mount (same pattern as
  // UserDetailsPage / ContentDetailsPage / MockChatPage).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Snapshot of `location.state` (carried by `Navigator.navigateToChat`),
  // taken ONCE at mount. `null` on the deep-link / refresh path — the hook
  // rehydrates from the URL `:peerId` param in that case.
  const initialChatUsersInfo = useMemo<ChatUsersInfoEntity | null>(() => {
    const state = location.state as ChatUsersInfoEntity | undefined;
    if (state && state.peerId && state.peerId === peerIdParam) {
      return state;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chat = useChat({
    peerIdParam: peerIdParam ?? '',
    initialChatUsersInfo,
  });

  // Sticky-chrome insets. The page is window-scrolled (review fix plan 4,
  // Finding 2): the app bar + no-internet banner are sticky at the top and the
  // composer is sticky at the bottom, both in document flow. Their measured
  // heights position the fixed overlay layer in `ChatMessagesRegion` and feed
  // `VirtualList`'s `reversedBottomPinInset` so a short message list pins just
  // above the composer with no blank gap (Finding 1). Measured (not hard-coded)
  // because the composer height is dynamic — multiline textarea, focus hides
  // the add-media/gift buttons, the blocked replacement, or fully hidden.
  // Declared before the loading gate to satisfy the Rules of Hooks; the
  // callback refs attach once the wrappers mount post-gate.
  const [topInset, topInsetRef] = useMeasuredHeight();
  const [bottomInset, bottomInsetRef] = useMeasuredHeight();

  // On-screen-keyboard inset (review fix plan 4, Finding 4). The sticky
  // composer is anchored to the bottom of the LAYOUT viewport; on iOS Safari /
  // installed PWAs the software keyboard shrinks only the VISUAL viewport, so
  // without this a `bottom: 0` composer would float behind the keyboard. We
  // raise the composer wrapper (`bottom: keyboardInset`) and pass the inset
  // into `ChatMessagesRegion`, which offsets the fixed overlay layer's bottom
  // and folds it into `VirtualList`'s window bottom-pin inset so a short list
  // pins just above the raised composer with no gap. `0` on desktop and when
  // no keyboard is shown — byte-for-byte unchanged in those cases.
  const keyboardInset = useKeyboardInset();

  // Page-only `useBasePage` — forwards each phase into the hook's exposed
  // callback. Hook callbacks are stable `useCallback` refs, so re-registration
  // is a no-op even though the lifecycle object is rebuilt each render.
  //
  // Scroll-position preservation across navigate-away / pop-back (review fix
  // plan 4, Finding 3) is owned by `RouteStack`'s `window.scrollY` restore —
  // NOT opted out of. Now that the page is window-scrolled (Finding 2), that
  // single source of truth applies to chat like every other page. The two
  // systems do not fight: under keep-alive the component stays mounted while
  // hidden, so the reversed `VirtualList`'s first-mount bottom-anchor is inert
  // on re-show (its `hasAnchoredToBottomRef` is already set), and `onPopNext`
  // (→ `chat.onPageVisible`) only re-claims presence — it does NOT refetch or
  // rebuild `messages`, so no fresh-identity list re-triggers the anchor or
  // wipes the restored position. `RouteStack` re-applies the saved scroll
  // across a short settle window so it survives the reversed list's late
  // layout growth on re-show (see its restore effect).
  useBasePage({
    screenKey,
    onInit: chat.onPageInit,
    onPopNext: chat.onPageVisible,
    onPushNext: chat.onPageHidden,
    onPop: chat.onPageHidden,
    onDispose: chat.onPageDispose,
  });

  // Title tap navigates to peer's user-details page (Flutter parity at
  // `chat_screen.dart:1145-1153`). Hook reports `chatUsersInfo` once
  // rehydration completes; the loading-gate below renders before then.
  const handleBack = useCallback((): void => {
    Navigator.goBackOr(() => Navigator.navigateToFeed());
  }, []);

  const handleTitleTap = useCallback((): void => {
    if (chat.chatUsersInfo) {
      Navigator.navigateToUserDetails(chat.chatUsersInfo.peerId);
    }
  }, [chat.chatUsersInfo]);

  // `MessageBubble` retry — forward to the hook's per-message retry. The
  // bubble already knows the exact failed message; the bulk variant
  // (`retryAllFailed`) is reserved for the reconnect path (Flutter
  // `chat_screen.dart:626-644`), which has no per-message context.
  const handleRetryMessage = useCallback(
    (message: ChatMessageEntity): void => {
      void chat.retryMessage(message);
    },
    [chat],
  );

  // Open the subscribe sheet for the peer. Mirrors the Flutter no-mutual-
  // subscription CTA at `chat_screen.dart:944-958`. Owned by the page (not
  // the hook) so the hook stays free of UI invocations — the page has
  // `screenKey` and forwards the hook's resolved `peerUser` so the sheet
  // does not re-fetch it.
  const handleSubscribePress = useCallback((): void => {
    if (!chat.chatUsersInfo) return;
    SubscribeUserSheet.show({
      targetFirebaseId: chat.chatUsersInfo.peerId,
      preloadedUser: chat.peerUser,
      screenKey,
    });
  }, [chat.chatUsersInfo, chat.peerUser, screenKey]);

  // `peerFirstName` is derived ONCE here (above the loading gate) so the
  // single value feeds both `handleMore` below and the post-gate render —
  // avoiding a duplicated `resolvePeerFirstName` call. Hoisted (rather than
  // moving `handleMore` post-gate) to keep the `useCallback` above the early
  // return and satisfy the Rules of Hooks. `chat.chatUsersInfo` may be null on
  // the loading path; the gate below short-circuits before any consumer reads
  // a meaningful value, and `peerName ?? ''` keeps this derivation null-safe.
  const peerFirstName = ChatUtils.resolvePeerFirstName(
    chat.peerUser,
    chat.chatUsersInfo?.peerName ?? '',
  );

  // Open the chat-actions sheet for the peer (Flutter "More" tap →
  // `ChatActionsBottomSheet`). Page-owned (not the hook) so the hook stays
  // free of UI invocations — the page has `screenKey` and reuses the already-
  // derived `peerFirstName` the sheet interpolates. Mirrors
  // `handleSubscribePress`.
  const handleMore = useCallback((): void => {
    if (!chat.chatUsersInfo) return;
    ChatActionsSheet.show({
      peerId: chat.chatUsersInfo.peerId,
      peerFirstName,
      // Live-flip the composer + app bar on a confirmed block/unblock without a
      // pop + re-navigate (Task 2). The setter is a stable `useChat` callback.
      onConversationBlockChanged: chat.setConversationBlocked,
      screenKey,
    });
  }, [chat.chatUsersInfo, chat.setConversationBlocked, peerFirstName, screenKey]);

  // Caller-initiate video call (Flutter `chat_screen.dart:1212-1234`). The app
  // bar already enforces the block / subscription visibility guards
  // (`ChatAppBar` `showVideoCall`), so the handler just adds the confirm +
  // launch. Page-owned (not the hook) so the hook stays free of UI invocations
  // — the page has `screenKey`, the auth `currentUser` (the caller), the
  // resolved `peerUser` (the audience), and the raw `l10n` the launch helper
  // threads through. On confirm we mint a fresh `callMessageId` (Flutter
  // `messageId = uuid.v1()`, `:1232`) and launch as the caller with no
  // `callerUserStreamingId` (parity `:1233` — caller passes `null`).
  const handleVideoCall = useCallback(async (): Promise<void> => {
    if (!currentUser || !chat.peerUser || !chat.chatUsersInfo) return;
    const description = l10n.chat.videoCallPage.confirmStartDescription.replace(
      /\{name\}/g,
      chat.peerUser.name,
    );
    const response = await ConfirmSheet.show({
      title: l10n.chat.videoCallPage.confirmEndTitle,
      confirmText: description,
      okText: l10n.common.yes,
      cancelText: l10n.common.cancel,
      screenKey,
    });
    if (!response) return;
    await launchVideoCall({
      callerUser: currentUser,
      audienceUser: chat.peerUser,
      chatUsersInfo: chat.chatUsersInfo,
      callMessageId: generateUuidV1(),
      callerUserStreamingId: undefined,
      l10n,
    });
  }, [currentUser, chat.peerUser, chat.chatUsersInfo, l10n, screenKey]);

  // Audience-join video call (Flutter `chat_screen.dart:2899-2924`). Fired by a
  // LIVE peer `videoCall` bubble tap (the bubble already guards `isLive`,
  // parity `:2901`). Page-owned for the same reasons as `handleVideoCall` — the
  // page has `screenKey`, the auth `currentUser`, the resolved `peerUser`, the
  // `chatUsersInfo` (the Agora channel), and the raw `l10n`. CRITICAL: the
  // launch argument order is INVERTED vs the caller path — here the PEER is the
  // caller and the CURRENT user is the audience (the joiner), `callMessageId`
  // comes from the existing message's `videoCallId` (not a fresh uuid), and
  // `callerUserStreamingId` is read off the message (parity `:2918-2923`). No
  // confirm cancel → no launch.
  const handleVideoCallJoin = useCallback(
    async (message: ChatMessageEntity): Promise<void> => {
      if (!currentUser || !chat.peerUser || !chat.chatUsersInfo) return;
      if (!message.videoCallId) return;
      const description =
        l10n.chat.videoCallPage.confirmStartDescription.replace(
          /\{name\}/g,
          chat.peerUser.name,
        );
      const response = await ConfirmSheet.show({
        title: l10n.chat.videoCallPage.confirmEndTitle,
        confirmText: description,
        okText: l10n.common.yes,
        cancelText: l10n.common.cancel,
        screenKey,
      });
      if (!response) return;
      await launchVideoCall({
        callerUser: chat.peerUser,
        audienceUser: currentUser,
        chatUsersInfo: chat.chatUsersInfo,
        callMessageId: message.videoCallId,
        callerUserStreamingId: message.callerStreamingId,
        l10n,
      });
    },
    [currentUser, chat.peerUser, chat.chatUsersInfo, l10n, screenKey],
  );

  // Loading gate — auth-hydrate OR hook rehydrate in flight.
  if (!currentUser || !chat.chatUsersInfo) {
    return (
      <div
        {...qaAttr({ id: 'chat', status: 'loading' })}
        style={{
          width: '100%',
          height: '100vh',
          backgroundColor: theme.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  // Derived render data (gated on resolved `chatUsersInfo`).
  const chatUsersInfo = chat.chatUsersInfo;
  const blockedVariant = ChatUtils.pickBlockedVariant(
    chat.currentUserBlocked,
    chat.currentUserBlockedByPeer,
    chat.peerUserBlocked,
  );
  const isBlockedForAppBar =
    chat.currentUserBlocked || chat.currentUserBlockedByPeer;
  // `peerFirstName` is derived once above the loading gate (single source) and
  // reused here for the render and below.
  // Preview the NEWEST unread item (the bucket is appended in arrival order
  // via `setUnreadItems(prev => [...prev, incoming])` in
  // `useChatLiveListener.handleAdded`, so the most recent peer message is at
  // the end). Mirrors Flutter's `_lastUpdatedChatMessage` — the pill always
  // shows what just arrived, not the oldest still-bucketed unread.
  const unreadPreview: string | null =
    chat.unreadItems.length === 0
      ? null
      : expauseLocalizations.getUnreadPreview(
          chat.unreadItems[chat.unreadItems.length - 1],
          peerFirstName,
        );
  // Mirrors Flutter `chat_screen.dart:991-992` (`_endToEndEncryptedWidget`):
  // gated on `_peerUser != null && !_hasMore && _listItems.isEmpty`. The
  // `peerUser !== null` guard prevents the empty-encrypted placeholder from
  // flashing before the peer resolves (it is fetched separately inside the
  // hook's initial load and can briefly be null even once `chatUsersInfo` is
  // set, or stay null if the peer fetch fails).
  const showEmptyState =
    chat.peerUser !== null && !chat.hasMore && chat.messages.length === 0;
  const isScrolledUp = chat.isScrolledUp || chat.unreadCount > 0;

  // Page-root lifecycle token for the qa-tester. The loading branch above
  // already emits `loading`; here the page is hydrated, so it is either
  // `blocked` (a full-screen blocked overlay covers the surface) or `ready`.
  // Reuses already-computed state — no new render gate is introduced.
  const pageQaStatus = blockedVariant !== null ? 'blocked' : 'ready';

  return (
    // Window-scrolled shell (review fix plan 4, Finding 2). No `100vh` /
    // `overflow: hidden` lock — the document scrolls, so `VirtualList` falls
    // through to `window` and the page-right document scrollbar renders
    // (review obs. 1; `html, body` already set `scrollbar-gutter: stable` so
    // there is no layout shift). The sticky app bar / composer sit in document
    // flow, so `document.documentElement.scrollHeight` includes their boxes and
    // the at-bottom / scroll-to-bottom math stays correct.
    <div
      {...qaAttr({ id: 'chat', status: pageQaStatus })}
      style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        backgroundColor: theme.background,
      }}
    >
      {/* 1+2. Sticky top group: app bar (+ no-internet banner). Sticky-in-flow
            so the browser reserves the top inset (same technique as
            `FlexibleHeaderList`). Folds in `responsive.safeArea.top` — a minor,
            intentional improvement over the old layout, which ignored it.
            Measured via `topInsetRef` → fixed overlay layer + window bottom-pin
            inset. */}
      <div
        ref={topInsetRef}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          paddingTop: responsive.safeArea.top,
          backgroundColor: theme.background,
        }}
      >
        {/* App bar — chat_screen.dart:1115-1284. */}
        <ChatAppBar
          peerUser={chat.peerUser}
          isBlocked={isBlockedForAppBar}
          isConversationBlocked={chat.isConversationBlocked}
          isSubscribedMutual={chat.isSubscribedMutual}
          onBack={handleBack}
          onTitleTap={handleTitleTap}
          onVideoCallTap={() => void handleVideoCall()}
          onMoreTap={handleMore}
          screenKey={screenKey}
        />

        {/* No-internet banner — chat_screen.dart:3745-3768. Stacked directly
            under the app bar in the same sticky-top group so it contributes to
            the measured top inset when visible. */}
        {!chat.isOnline && <NoInternetBanner />}
      </div>

      {/* 3. Messages region (flows in the document) + fixed overlay layer —
            see `ChatMessagesRegion`. Receives the measured insets to size the
            overlay band and the window bottom-pin inset. */}
      <ChatMessagesRegion
        qa={{ id: 'chat-messages' }}
        messages={chat.messages}
        peerUser={chat.peerUser}
        currentUserId={currentUser.firebaseId}
        openTimestampForMessageId={chat.openTimestampForMessageId}
        onToggleTimestamp={chat.toggleTimestamp}
        onRetry={handleRetryMessage}
        onVideoCallJoin={(message) => void handleVideoCallJoin(message)}
        onLoadMore={chat.onLoadMore}
        onScrolledToBottom={chat.onScrolledToBottom}
        onScrolledAway={chat.onScrolledAway}
        myLastReadPosition={chat.myLastMessageReadPosition}
        screenKey={screenKey}
        isSubscribedMutual={chat.isSubscribedMutual}
        peerName={chatUsersInfo.peerName}
        onSubscribePress={handleSubscribePress}
        showEmptyState={showEmptyState}
        peerFirstName={peerFirstName}
        blockedVariant={blockedVariant}
        unreadPreview={unreadPreview}
        unreadBannerVisible={chat.unreadBannerVisible}
        unreadCount={chat.unreadCount}
        isScrolledUp={isScrolledUp}
        sendError={chat.sendError}
        isOnline={chat.isOnline}
        onSendErrorDismiss={() => {}}
        isFirstLoad={chat.isFirstLoad}
        isFetchingMore={chat.isFetchingMore}
        scrollToBottomToken={chat.scrollToBottomToken}
        topInset={topInset}
        bottomInset={bottomInset}
        keyboardInset={keyboardInset}
      />

      {/* 4. Composer — chat_screen.dart:3538-3565. Sticky-in-flow at the
            document end so the newest bubble lands just above it at max scroll.
            Folds in `responsive.safeArea.bottom`. The wrapper stays mounted for
            a stable measured height even when `ChatComposer` self-hides (it
            returns its OWN content as `null`, collapsing the wrapper to just the
            safe-area padding). See `ChatComposer` JSDoc for the branch priority
            (hidden / blocked / normal).

            `bottom: keyboardInset` (review fix plan 4, Finding 4): a sticky
            footer anchors to the bottom of the LAYOUT viewport, which the iOS
            software keyboard does not shrink — so it raises the composer above
            the keyboard. `0` (no shift) on desktop / when no keyboard is shown.
            `useKeyboardInset` listens to `visualViewport`, so the composer
            tracks the keyboard as it animates in/out. */}
      <div
        ref={bottomInsetRef}
        style={{
          position: 'sticky',
          bottom: keyboardInset,
          zIndex: 50,
          paddingBottom: responsive.safeArea.bottom,
          backgroundColor: theme.background,
        }}
      >
        <ChatComposer
          isSubscribedMutual={chat.isSubscribedMutual}
          isConversationBlocked={chat.isConversationBlocked}
          onSendText={chat.sendText}
          onSendMedia={chat.sendMedia}
          chatUsersInfo={chatUsersInfo}
          peerUser={chat.peerUser}
          isFirstLoad={chat.isFirstLoad}
          screenKey={screenKey}
        />
      </div>
    </div>
  );
};
